import type { DiffChange } from '../types/amendmentReview';

/** Formatting shared by the amendment review and the structured form. */

export const formatDate = (value: unknown): string => {
  if (!value) return '—';
  const text = String(value).slice(0, 10);
  const date = new Date(`${text}T12:00:00`);
  return Number.isNaN(date.getTime()) ? String(value)
    : date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value: unknown): string => {
  if (!value) return '—';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value)
    : date.toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const MONEY_FIELDS = /amount|commitment|balance|base|reserve|reduction|availability|drawn|pending|booked|amended|delta|result/i;
const PCT_FIELDS = /pct|rate/i;

export const formatNumber = (value: unknown, digits = 2): string => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value ?? '—');
  return number.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: digits });
};

/** A value for display, typed by its field name when the server gives none. */
export const formatValue = (value: unknown, field?: string): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text) && field && /date|from|to|start|end/i.test(field)) return formatDate(text);
  const number = Number(text.replace(/,/g, ''));
  if (Number.isFinite(number) && text.trim() !== '' && /^-?[\d,.]+$/.test(text.trim())) {
    if (field && /bps/i.test(field)) return `${formatNumber(number, 2)} bps`;
    if (field && PCT_FIELDS.test(field) && !/bps/i.test(field)) return `${formatNumber(number, 4)}%`;
    if (field && MONEY_FIELDS.test(field)) return formatNumber(number, 2);
    return formatNumber(number, 6);
  }
  return text.replace(/_/g, ' ');
};

export const signed = (value: unknown): string => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number >= 0 ? '+' : '−'}${formatNumber(Math.abs(number), 2)}`;
};

export const fieldLabel = (field: string): string => field
  .replace(/_/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/\bpct\b/i, '%')
  .replace(/\bbps\b/i, 'bps')
  .replace(/^./, first => first.toUpperCase());

export const CHANGE_LABEL: Record<DiffChange, string> = {
  ADDED: 'Added', REMOVED: 'Removed', CHANGED: 'Changed', CARRIED: 'Carried',
};

export const CHANGE_COLOUR: Record<DiffChange, string> = {
  ADDED: '#7cddbe', REMOVED: '#f8a5a5', CHANGED: '#edcc82', CARRIED: '#a8b6ca',
};
